import { Alert, AlertColor, Snackbar } from "@mui/material";
import { createContext, FC, ReactNode, useCallback, useMemo, useRef, useState } from "react";

export type ToastContextProps = {
  addToast: ({ text, type }: ToastType) => void;
};

export const ToastContext = createContext<ToastContextProps>({
  addToast: () => {},
});

export enum ToastTypeEnum {
  SUCCESS = "success",
  WARNING = "warning",
  ERROR = "error",
}

export type ToastType = { text?: string; type: AlertColor };
type ToastItem = ToastType & { id: number };

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastIdRef = useRef(0);

  const removeToast = useCallback((id: number) => {
    setToasts((prevState) => prevState.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(({ text, type }: ToastType) => {
    const nextId = toastIdRef.current;
    toastIdRef.current += 1;

    if (!text) {
      switch (type) {
        case ToastTypeEnum.SUCCESS: {
          setToasts((prevState) => [...prevState, { id: nextId, text: "Успешно сохранено", type }]);
          return;
        }
        case ToastTypeEnum.WARNING: {
          setToasts((prevState) => [...prevState, { id: nextId, text: "Возможно, произошла какая-то ошибка", type }]);
          return;
        }
        case ToastTypeEnum.ERROR:
        default: {
          setToasts((prevState) => [...prevState, { id: nextId, text: "Произошла ошибка. Данные не сохранены", type }]);
          return;
        }
      }
    }

    setToasts((prevState) => [...prevState, { id: nextId, text, type }]);
  }, []);

  const providerValue = useMemo(() => ({ addToast }), [addToast]);

  return (
    <ToastContext.Provider value={providerValue}>
      {children}
      {toasts.map((toast, index) => (
        <Snackbar
          key={toast.id}
          open
          autoHideDuration={5000}
          onClose={() => removeToast(toast.id)}
          sx={{ marginTop: 6 + index * 7, background: "white", borderRadius: "10px" }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}>
          <Alert
            variant='outlined'
            severity={toast.type}
            onClose={() => removeToast(toast.id)}
            sx={{
              height: "auto",
              "& .MuiAlert-message": {
                py: 1,
                lineHeight: 1.5,
              },
              "& .MuiAlert-icon": {
                py: 1,
              },
            }}>
            {toast.text}
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
};
